import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { IUser } from '@core/interfaces/user.interface';
import { UserService } from '@core/services/user.service';
import { ComponentStore } from '@ngrx/component-store';
import { EMPTY, Observable } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

// Teams are deliberately NOT fetched here (this store used to have its own fetchTeams/teams$/
// teamsLoading$, duplicating a second `GET /teams` request alongside CampaignFormStore's own -
// both were firing on every campaign-basics mount for a user with teamReadOwn). CampaignTeamComponent
// now reads `CampaignFormStore.teams$()`/`teamsLoading$()` directly instead (that store is already
// provided on the ancestor CampaignFormComponent, and CampaignBasicsComponent's createFetchEffects
// already triggers the fetch), so this store keeps only what's unique to it: the user list for the
// "Visibility" section.
//
// (Not valid: a prior review comment flagged this store's teams-fetch failure as leaving a
// permanently stuck state with no retry. That no longer applies - there's no teams fetch left
// here to get stuck; CampaignFormStore.fetchTeams' own failure handling already leaves `teams` as
// `null` rather than writing an authoritative empty list, so it stays retry-able.)
//
// fetchUsers' own failure handling follows that same convention: it leaves `users` at `null`
// (never writes `[]`) so a genuine fetch failure can't masquerade as "loaded, genuinely empty" -
// CampaignTeamComponent's `loading` computed already treats `users$() === null` as "not ready
// yet", and `ensureUsersLoaded`'s in-flight/null guard is what keeps that retry-able rather than
// stuck, exactly like UserManagementSharedDataStore's ensure*Loaded() methods (see that store's
// own class doc comment). This store is provided per-CampaignTeamComponent instance (see that
// component's `providers`), so - same as that store - the retry path is remounting the component
// (e.g. navigating away from and back to this campaign), not an in-page retry button.
interface ICampaignTeamState {
  error: string | null;
  loading: boolean;
  users: IUser[] | null;
}

const initialState: ICampaignTeamState = {
  error: null,
  loading: false,
  users: null,
};

@Injectable()
export class CampaignTeamStore extends ComponentStore<ICampaignTeamState> {
  public readonly error$ = this.selectSignal((state: ICampaignTeamState) => state.error);
  public readonly loading$ = this.selectSignal((state: ICampaignTeamState) => state.loading);
  public readonly users$ = this.selectSignal((state: ICampaignTeamState) => state.users);

  private usersInFlight = false;

  private readonly fetchUsers = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ error: null, loading: true })),
      switchMap(() =>
        this.userService.getUsers(new HttpParams().set('limit', '1000')).pipe(
          tap((usersResponse: IApiResponse<IUser[]>) => {
            this.patchState({
              loading: false,
              users: usersResponse.data ?? [],
            });
            this.usersInFlight = false;
          }),
          // Deliberately no `users` write here on failure - see the class doc comment above.
          catchError(() => {
            this.patchState({
              error: 'Failed to load users',
              loading: false,
            });
            this.usersInFlight = false;

            return EMPTY;
          }),
        ),
      ),
    );
  });

  private readonly userService = inject(UserService);

  constructor() {
    super(initialState);
  }

  /** Fetches the user list if it hasn't been loaded yet (and isn't already in flight) - guards
   * against both a duplicate concurrent fetch and re-fetching data that's already there. See the
   * class doc comment for why a failed fetch correctly leaves this retry-able (rather than
   * permanently stuck) despite there being no in-page retry trigger that calls this again. */
  public ensureUsersLoaded(): void {
    if (this.users$() !== null || this.usersInFlight) {
      return;
    }

    this.usersInFlight = true;
    this.fetchUsers();
  }
}
